"use client";
import { ResourceType } from "@gettactic/api";
import { getResourceTypeGroup } from "@gettactic/helpers/src/reserve/resourceTypeGroup";
import { useMapSchedulable } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { addMinutes, compareAsc, format, parseISO } from "date-fns";
import { api } from "@/lib/api/api";

type UseResKey = {
  officeId: string;
  durationMins: number;
  // Attention! This may not be week days actually, but multi-date picker's dates.
  weekDatesStr: string[];
  resourceType: ResourceType;
  startTime: Date;
  filterParent?: string | null;
  organizer?: string | null;
  enabled?: boolean;
};

// We do pass weekDatesStr, which is multiple days,
// but we only get data for a single day (first one).
export function useReserveResources({
  officeId,
  durationMins,
  weekDatesStr,
  resourceType,
  startTime,
  filterParent,
  organizer = null,
  enabled = true
}: UseResKey) {
  // single date component used for meeting rooms only
  const isSingleDate = resourceType === "meeting_room";

  const { data: office } = useQuery({
    queryKey: ["organizations.offices", officeId],

    queryFn: async () => {
      const res = await api.client.organizations.office(officeId!);
      return res.result;
    },

    enabled: !!officeId
  });

  const { startDateAndTime, endDateAndTime } = getStartEndDate(
    weekDatesStr,
    startTime,
    durationMins,
    isSingleDate
  );

  // The map already loads every schedulable resource on this floor for this
  // window, so asking for the same superset lets this list come from that cache
  // entry instead of a second, narrower request for a subset of it.
  const { schedulable, isPending } = useMapSchedulable({
    officeId,
    parentId: filterParent ?? null,
    startDate: startDateAndTime,
    endDate: endDateAndTime,
    organizerId: organizer,
    enabled: enabled && !!officeId
  });

  const types = getResourceTypeGroup(resourceType);
  // Keep the loading state as `undefined` rather than an empty list —
  // consumers distinguish "not loaded yet" from "no resources available".
  const resourcesList = isPending
    ? undefined
    : schedulable.filter((resource) => types.includes(resource.type));

  const { data: parkingResourcesList } = useQuery({
    queryKey: [
      "organizations.schedulable",
      officeId,
      weekDatesStr,
      getResourceTypeGroup("parking_space"),
      startTime,
      durationMins,
      organizer
    ],

    queryFn: async () => {
      const startFormatted = format(startTime, "HH:mm:ss");
      const dates = weekDatesStr.map((w) => {
        return `${w}T${startFormatted}.000Z`;
      });

      const { startDateAndTime, endDateAndTime } = getStartEndDate(
        weekDatesStr,
        startTime,
        durationMins,
        isSingleDate
      );
      const res = await api.client.organizations.getSchedulableResources({
        officeId: officeId!,
        types: getResourceTypeGroup("parking_space"),
        startDate: startDateAndTime,
        endDate: endDateAndTime,
        duration: durationMins.toString(),
        manage: false,
        organizerId: organizer
      });
      return res.result?.available || [];
    },

    enabled:
      enabled &&
      !!officeId &&
      !!office &&
      office.total_parking_spaces + office.total_parking_lots > 0
  });

  // Walk-up resources are never reservable from the app — their occupancy
  // is managed externally via the API — so they must not appear as a
  // pickable option in the browse-and-book resource list.
  return {
    resourcesList: resourcesList?.filter((resource) => !resource.is_walkup),
    office,
    parkingResourcesList: parkingResourcesList?.filter(
      (resource) => !resource.is_walkup
    )
  };
}

/**
 * @param weekDatesStr - Attention! This are not week days actually,
 *                       but multi-date datepicker's dates.
 *                       It should be used to get the earliest date.
 * @param startTime - The start time
 * @param durationMins - The duration in minutes
 * @param isSingleDate - Whether the resource is a meeting room, it's important
 *                        because is the only reliable way to determine where we get date info from,
 *                        because in case of meeting room, we get data for a single day (startDateAndTime),
 *                        but in case of other resources, we get data for multiple days (weekDatesStr).
 * @returns The start date and time and the end date and time
 */
export const getStartEndDate = (
  weekDatesStr: string[],
  startTime: Date,
  durationMins: number,
  isSingleDate: boolean
) => {
  if (!isSingleDate) {
    const earliestDate = Array.from(weekDatesStr).sort((a, b) =>
      compareAsc(parseISO(a), parseISO(b))
    )[0];
    const startTimeFormatted = format(startTime, "HH:mm:ss");

    const endTimeFormatted = format(
      addMinutes(startTime, durationMins),
      "HH:mm:ss"
    );

    // user can schedule late in the day, so reservation can end on the next day
    const earliestDateWithTime = parseISO(
      `${earliestDate}T${startTimeFormatted}`
    );
    const endDate = format(
      addMinutes(earliestDateWithTime, durationMins),
      "yyyy-MM-dd"
    );
    const answer = {
      startDateAndTime: `${earliestDate}T${startTimeFormatted}Z`,
      endDateAndTime: `${endDate}T${endTimeFormatted}Z`
    };
    return answer;
  }
  const startTimeFormatted = format(startTime, "HH:mm:ss");
  const endTimeFormatted = format(
    addMinutes(startTime, durationMins),
    "HH:mm:ss"
  );

  // user can schedule late in the day, so reservation can end on the next day.
  // The end day is counted from the start's day, not startTime's: right after
  // the date changes, startTime still holds the previous day for a render.
  const startDateWithTime = parseISO(
    `${weekDatesStr[0]}T${startTimeFormatted}`
  );
  const endDate = format(
    addMinutes(startDateWithTime, durationMins),
    "yyyy-MM-dd"
  );
  return {
    startDateAndTime: `${weekDatesStr[0]}T${startTimeFormatted}Z`,
    endDateAndTime: `${endDate}T${endTimeFormatted}Z`
  };
};
